// Phase 3 — بنك المشاكل (problem catalog).
// Public read (SSR-safe for /book), staff CRUD, and a "sync options from
// catalog" server fn that re-materializes the `main_problem` item options
// inside `consultation-intake.schema.sections[0].items`.

import { DIAG_SHARED_ITEMS, MAIN_PROBLEM_HELP_AR, MAIN_PROBLEM_MAX, prefixDiagSection } from "@/lib/intake-diag";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { serverPublic } from "@/integrations/supabase/server-public";

async function assertStaff(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin") && !roles.includes("assistant")) throw new Error("Forbidden");
  return { isAdmin: roles.includes("admin") };
}
async function assertAdmin(supabase: any, userId: string) {
  const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  const roles = (data ?? []).map((r: { role: string }) => r.role);
  if (!roles.includes("admin")) throw new Error("Forbidden: admin only");
}
// -------- Public read (used by the booking wizard) --------
export const listActiveProblems = createServerFn({ method: "GET" }).handler(async () => {
  const sb = serverPublic();
  const { data, error } = await sb
    .from("problem_catalog")
    .select("id, slug, label_ar, order, age_bands, diagnostic_section_ref, suggested_pillars")
    .eq("is_active", true)
    .order("order", { ascending: true });
  if (error) throw error;
  return data ?? [];
});

// -------- Staff read (all rows) --------
export const listProblemCatalogAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertStaff(context.supabase, context.userId);
    const { data, error } = await context.supabase
      .from("problem_catalog")
      .select("id, slug, label_ar, order, age_bands, diagnostic_section_ref, suggested_pillars, is_active, updated_at")
      .order("order", { ascending: true });
    if (error) throw error;
    return data ?? [];
  });

// -------- Admin CRUD --------
const upsertInput = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(2).regex(/^[a-z0-9_]+$/, "المسموح أحرف إنجليزية صغيرة و_ فقط"),
  label_ar: z.string().min(1),
  order: z.number().int().min(0).default(0),
  age_bands: z.array(z.string()).default([]),
  diagnostic_section_ref: z.string().nullable().optional(),
  suggested_pillars: z.array(z.string()).default([]),
  is_active: z.boolean().default(true),
});

export const upsertProblem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => upsertInput.parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const patch = {
      slug: data.slug,
      label_ar: data.label_ar,
      order: data.order,
      age_bands: data.age_bands,
      diagnostic_section_ref: data.diagnostic_section_ref ?? null,
      suggested_pillars: data.suggested_pillars,
      is_active: data.is_active,
    };
    if (data.id) {
      const { error } = await context.supabase.from("problem_catalog").update(patch).eq("id", data.id);
      if (error) throw error;
      return { id: data.id };
    }
    const { data: row, error } = await context.supabase
      .from("problem_catalog")
      .insert(patch)
      .select("id")
      .single();
    if (error) throw error;
    return { id: row.id };
  });

export const deleteProblem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((d: unknown) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertAdmin(context.supabase, context.userId);
    const { error } = await context.supabase.from("problem_catalog").delete().eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

// -------- Sync main_problem options into consultation-intake schema --------
export const syncIntakeMainProblemOptions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context.supabase, context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: catalog, error: cErr } = await supabaseAdmin
      .from("problem_catalog")
      .select("slug, label_ar, diagnostic_section_ref")
      .eq("is_active", true)
      .order("order", { ascending: true });
    if (cErr) throw cErr;
    const catalogRows = (catalog ?? []) as Array<{
      slug: string;
      label_ar: string;
      diagnostic_section_ref: string | null;
    }>;
    const options = catalogRows.map((r) => r.label_ar);

    const { data: form, error: fErr } = await supabaseAdmin
      .from("forms")
      .select("id, schema")
      .eq("slug", "consultation-intake")
      .maybeSingle();
    if (fErr) throw fErr;
    if (!form) throw new Error("consultation-intake not found");

    const schema: any = form.schema ?? {};
    let sections: any[] = Array.isArray(schema.sections) ? [...schema.sections] : [];
    const s1Idx = sections.findIndex((s) => s?.id === "s1_child_info");
    if (s1Idx === -1) throw new Error("s1_child_info section missing");
    const s1 = { ...sections[s1Idx], items: [...(sections[s1Idx].items ?? [])] };
    const mainIdx = s1.items.findIndex((it: any) => it?.id === "main_problem");
    if (mainIdx === -1) throw new Error("main_problem item missing — run Phase 3 migration first");
    s1.items[mainIdx] = {
      ...s1.items[mainIdx],
      options,
      type: "multi_choice",
      max: MAIN_PROBLEM_MAX,
      help_ar: MAIN_PROBLEM_HELP_AR,
    };
    sections[s1Idx] = s1;

    // Re-gate existing diagnostic sections by current label_ar (keyed by stable
    // diagnostic_section_ref) and append new sections for any active catalog row
    // whose diagnostic section is missing. Mirrors the Phase 3 migration shape.
    const sharedItems: any[] = DIAG_SHARED_ITEMS;

    let sectionsAdded = 0;
    let sectionsRegated = 0;
    for (const row of catalogRows) {
      const sectionRef = row.diagnostic_section_ref || `diag_${row.slug}`;
      const existingIdx = sections.findIndex((s) => s?.id === sectionRef);
      if (existingIdx !== -1) {
        const sec = sections[existingIdx];
        const currentValues: string[] = Array.isArray(sec?.visible_when?.values) ? sec.visible_when.values : [];
        if (currentValues.length !== 1 || currentValues[0] !== row.label_ar) {
          sections[existingIdx] = {
            ...sec,
            visible_when: { item_id: "main_problem", values: [row.label_ar] },
          };
          sectionsRegated++;
        }
        continue;
      }
      // Append new diagnostic section for a newly-added catalog row.
      sections.push({
        id: sectionRef,
        title_ar: `أسئلة تشخيصية: ${row.label_ar}`,
        intro_ar: "أجب عن هذه الأسئلة الموجّهة تحديدًا لهذه المشكلة — سيستخدمها د. سارة أثناء الاستشارة.",
        _content_status: "draft-dr-sara",
        visible_when: { item_id: "main_problem", values: [row.label_ar] },
        items: [
          ...sharedItems,
          { id: `${row.slug}_specific_1`, type: "long_text", label_ar: `سؤال تشخيصي خاص (1) — ${row.label_ar}`, required: false, _placeholder: true, _content_status: "draft-dr-sara" },
          { id: `${row.slug}_specific_2`, type: "long_text", label_ar: `سؤال تشخيصي خاص (2) — ${row.label_ar}`, required: false, _placeholder: true, _content_status: "draft-dr-sara" },
          { id: `${row.slug}_specific_3`, type: "long_text", label_ar: `سؤال تشخيصي خاص (3) — ${row.label_ar}`, required: false, _placeholder: true, _content_status: "draft-dr-sara" },
        ],
      });
      sectionsAdded++;
    }

    // Several diag sections can be visible at once: give each its own item ids.
    sections = sections.map((sec) => prefixDiagSection(sec));
    const updated = { ...schema, sections, _synced_at: new Date().toISOString() };
    const { error: uErr } = await supabaseAdmin.from("forms").update({ schema: updated }).eq("id", form.id);
    if (uErr) throw uErr;

    return { ok: true, option_count: options.length, sections_added: sectionsAdded, sections_regated: sectionsRegated };
  });